import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import {
  getWebsiteWithConnections,
  deleteLink,
  addLink,
  updateWebsite,
  deleteWebsite,
  fetchAdminWebsiteSecret,
} from "../lib/supabase/websites";
import { useUser } from "../context/UserContext";
import { fetchWebsites } from "../lib/supabase/websites";
import { WebsiteWithConnections, Website } from "../types/website";
import { formatDate } from "../utils/dateUtils";
import { supabase } from "../lib/supabase";
import { WebsiteInfoPanel, StatusInfoPanel, NetworkConnectionsPanel, QuickActionsPanel, FrontendInfoPanel } from "../components/websiteDetail";
import DNSInfoPanel from "../components/websiteDetail/DNSInfoPanel";
import HostingInfoPanel from "../components/websiteDetail/HostingInfoPanel";
import TasksPanel from "../components/websiteDetail/TasksPanel";
import WebsiteHeader from "../components/websiteDetail/WebsiteHeader";
import WebsiteDropdown from "../components/WebsiteDropdown";
import { Button, Badge, Icon, Tabs, SidePanel } from "../components/ui";
import type { BadgeTone } from "../components/ui";

const SECTIONS = ['Website', 'DNS', 'Hosting', 'Frontend', 'Status', 'Connections', 'Tasks'];

const SECTION_IDS: Record<string, string> = {
  Website: 'info',
  DNS: 'dns',
  Hosting: 'hosting',
  Frontend: 'frontend',
  Status: 'status',
  Connections: 'network',
  Tasks: 'tasks',
};

const SECTION_NAMES: Record<string, string> = {
  info: 'Website',
  dns: 'DNS',
  hosting: 'Hosting',
  frontend: 'Frontend',
  status: 'Status',
  network: 'Connections',
  tasks: 'Tasks',
};

const STATUS_TONES: Record<string, BadgeTone> = {
  Pending: 'info',
  'In Progress': 'warn',
  Complete: 'up',
  Live: 'up',
  Indexed: 'up',
  Submitted: 'info',
  Partial: 'warn',
  Issues: 'down',
  Failed: 'down',
};

export default function WebsiteDetail() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const isAdmin = user?.role === 'admin';
  const [website, setWebsite] = useState<WebsiteWithConnections | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState<boolean>(false);
  const [availableWebsites, setAvailableWebsites] = useState<Website[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string>("");
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] =
    useState<boolean>(false);
  const [showCustomColor, setShowCustomColor] = useState<boolean>(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('info');
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const [editForm, setEditForm] = useState({
    url: "",
    title: "",
    category: "",
    color: "#d95636",
    registrar: "",
    registrar_username: "",
    registrar_password: "",
    dns_host: "",
    nameservers: [] as string[],
    hosting_provider: "",
    hosting_username: "",
    hosting_password: "",
    hosting_location: "",
    ip_address: "",
    frontend_platform: "",
    frontend_theme: "",
    search_console_access: "",
    build_status: "Pending",
    frontend_username: "",
    frontend_password: "",
    deployment_status: "Pending",
    indexing_status: "Not Indexed",
    last_content_update: "",
  });

  const loadWebsite = useCallback(async () => {
    if (!websiteId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await getWebsiteWithConnections(websiteId);
      const secret = isAdmin && data ? await fetchAdminWebsiteSecret(data.id) : null;
      if (data && secret) {
        data.hosting_password = secret.hosting_password;
        data.frontend_password = secret.frontend_password;
        data.registrar_password = secret.registrar_password;
      }
      setWebsite(data);

      if (data) {
        setEditForm({
          url: data.url,
          title: data.title || "",
          category: data.category || "",
          color: data.color || "#d95636",
          registrar: data.registrar || "",
          registrar_username: data.registrar_username || "",
          registrar_password: secret?.registrar_password || "",
          dns_host: data.dns_host || "",
          nameservers: Array.isArray(data.nameservers) ? data.nameservers : [],
          hosting_provider: data.hosting_provider || "",
          hosting_username: data.hosting_username || "",
          hosting_password: secret?.hosting_password || "",
          hosting_location: data.hosting_location || "",
          ip_address: data.ip_address || "",
          frontend_platform: data.frontend_platform || "",
          last_content_update: data.last_content_update ? formatDate(data.last_content_update, 'yyyy-MM-dd') : "",
          frontend_theme: data.frontend_theme || "",
          search_console_access: data.search_console_access || "",
          build_status: data.build_status || "Pending",
          deployment_status: data.deployment_status || "Pending",
          frontend_username: data.frontend_username || "",
          frontend_password: secret?.frontend_password || "",
          indexing_status: data.indexing_status || "Not Indexed",
        });
      }
    } catch (err) {
      console.error("Error loading website details:", err);
      setError("Failed to load website details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [websiteId, isAdmin]);

  const loadAvailableWebsites = useCallback(async () => {
    if (!websiteId) return;

    try {
      const allWebsites = await fetchWebsites();

      // Filter out the current website and already linked websites
      const linkedWebsiteIds = new Set(
        website?.outgoingLinks.map((link) => link.to_id) || [],
      );

      const filteredWebsites = allWebsites.filter(
        (site) => site.id !== websiteId && !linkedWebsiteIds.has(site.id),
      );

      setAvailableWebsites(filteredWebsites);

      // Set default selected website if available
      if (filteredWebsites.length > 0) {
        setSelectedTargetId(filteredWebsites[0].id);
      }
    } catch (err) {
      console.error("Error loading available websites:", err);
      setError("Failed to load available websites.");
    }
  }, [websiteId, website]);

  useEffect(() => {
    if (websiteId) {
      loadWebsite();
    }
  }, [websiteId, loadWebsite]);

  useEffect(() => {
    if (isAddLinkModalOpen) {
      loadAvailableWebsites();
    }
  }, [isAddLinkModalOpen, loadAvailableWebsites]);

  const handleSaveEdit = async () => {
    if (!websiteId) return;

    // Validate URL
    if (!editForm.url.trim()) {
      setError("URL cannot be empty");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Extract the fields that can be updated via the updateWebsite function
      const basicFields = {
        url: editForm.url,
        title: editForm.title || null,
        category: editForm.category || null,
        color: editForm.color,
      };

      // First update the basic website details
      await updateWebsite(websiteId, basicFields);

      // Then update the extended fields directly via supabase
      const { error: extendedFieldsError } = await supabase
        .from("websites")
        .update({
          registrar: editForm.registrar || null,
          registrar_username: editForm.registrar_username || null,
          ...(isAdmin ? {
            registrar_password: editForm.registrar_password || null,
            hosting_password: editForm.hosting_password || null,
            frontend_password: editForm.frontend_password || null,
          } : {}),
          dns_host: editForm.dns_host || null,
          nameservers: editForm.nameservers.length > 0 ? editForm.nameservers : null,
          hosting_provider: editForm.hosting_provider || null,
          hosting_username: editForm.hosting_username || null,
          hosting_location: editForm.hosting_location || null,
          ip_address: editForm.ip_address || null,
          frontend_platform: editForm.frontend_platform || null,
          last_content_update: editForm.last_content_update ? new Date(editForm.last_content_update + 'T12:00:00').toISOString() : null,
          frontend_theme: editForm.frontend_theme || null,
          search_console_access: editForm.search_console_access || null,
          build_status: editForm.build_status || "Pending",
          deployment_status: editForm.deployment_status || "Pending",
          frontend_username: editForm.frontend_username || null,
          indexing_status: editForm.indexing_status || "Not Indexed",
        })
        .eq("id", websiteId);

      if (extendedFieldsError) throw extendedFieldsError;

      await loadWebsite();
      setIsEditMode(false);
    } catch (err) {
      console.error("Error updating website:", err);
      setError("Failed to update website. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddLink = async () => {
    if (!websiteId || !selectedTargetId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await addLink(websiteId, selectedTargetId);
      setIsAddLinkModalOpen(false);
      await loadWebsite();
    } catch (err) {
      console.error("Error adding link:", err);
      setError("Failed to add link. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    setDeletingLinkId(linkId);

    try {
      await deleteLink(linkId);
      await loadWebsite();
    } catch (err) {
      console.error("Error deleting link:", err);
      setError("Failed to delete link. Please try again.");
    } finally {
      setDeletingLinkId(null);
    }
  };

  const handleDeleteWebsite = async () => {
    if (!websiteId) return;

    setIsSubmitting(true);

    try {
      await deleteWebsite(websiteId);
      navigate("/private-network");
    } catch (err) {
      console.error("Error deleting website:", err);
      setError("Failed to delete website. Please try again.");
    } finally {
      setIsSubmitting(false);
      setIsConfirmDeleteOpen(false);
    }
  };

  const formatUrl = (url: string): string => {
    // Remove protocol and trailing slash for display
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  };

  // List of predefined colors for the dropdown
  const predefinedColors = [
    { label: "Default (Orange)", value: "#d95636" },
    { label: "Blue", value: "#4361ee" },
    { label: "Green", value: "#10b981" },
    { label: "Purple", value: "#8b5cf6" },
    { label: "Teal", value: "#14b8a6" },
    { label: "Pink", value: "#ec4899" },
    { label: "Yellow", value: "#f59e0b" },
    { label: "Red", value: "#ef4444" },
    { label: "Custom", value: "custom" },
  ];

  const handleColorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "custom") {
      setShowCustomColor(true);
    } else {
      setShowCustomColor(false);
      setEditForm({ ...editForm, color: value });
    }
  };

  const getStatusBadge = (status: string) => (
    <Badge tone={STATUS_TONES[status] || 'idle'} dot>
      {status}
    </Badge>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center">
              <div
                className="w-10 h-10 rounded-full animate-spin mx-auto"
                style={{
                  border: '3px solid var(--border-hairline)',
                  borderTopColor: 'var(--accent)',
                  marginBottom: 'var(--space-4)',
                }}
              />
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading website...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!website && !isLoading) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[60vh]">
            <div
              className="text-center max-w-md"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-8)',
              }}
            >
              <div style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}>
                <Icon name="globe" size={28} />
              </div>
              <h2
                style={{
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  color: 'var(--text-body)',
                  marginBottom: 'var(--space-2)',
                }}
              >
                Website Not Found
              </h2>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-5)' }}>
                The website you're looking for doesn't exist or has been deleted.
              </p>
              <Link to="/private-network">
                <Button size="sm" icon="chevron-left">Back to Network</Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <WebsiteHeader
          website={website}
          isEditMode={isEditMode}
          isSubmitting={isSubmitting}
          formatUrl={formatUrl}
          setIsEditMode={setIsEditMode}
          handleSaveEdit={handleSaveEdit}
          statusBadge={website?.deployment_status ? getStatusBadge(website.deployment_status) : null}
        />

        {error && (
          <div
            style={{
              marginBottom: 'var(--space-5)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Seven tabs don't fit a phone: scroll them sideways instead of
            pushing Status / Connections / Tasks off the clipped page. The 1px
            bottom padding holds the active underline (bottom: -1px). */}
        <div className="scrollbar-hidden" style={{ overflowX: 'auto', paddingBottom: '1px', marginBottom: 'var(--space-6)' }}>
          <Tabs
            tabs={SECTIONS}
            active={expandedSection ? SECTION_NAMES[expandedSection] : ''}
            onChange={(tab) => toggleSection(SECTION_IDS[tab])}
            style={{ minWidth: 'max-content' }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: 'var(--space-5)' }}>
          <div className="lg:col-span-2">
            {expandedSection === 'info' && (
              <WebsiteInfoPanel
                website={website}
                isEditMode={isEditMode}
                editForm={editForm}
                setEditForm={setEditForm}
                showCustomColor={showCustomColor}
                setShowCustomColor={setShowCustomColor}
                predefinedColors={predefinedColors}
                handleColorChange={handleColorChange}
                formatUrl={formatUrl}
              />
            )}

            {expandedSection === 'dns' && (
              <DNSInfoPanel
                website={website}
                isEditMode={isEditMode}
                editForm={editForm}
                setEditForm={setEditForm}
              />
            )}

            {expandedSection === 'hosting' && (
              <HostingInfoPanel
                website={website}
                isEditMode={isEditMode}
                editForm={editForm}
                setEditForm={setEditForm}
              />
            )}

            {expandedSection === 'frontend' && (
              <FrontendInfoPanel
                website={website}
                isEditMode={isEditMode}
                editForm={editForm}
                setEditForm={setEditForm}
              />
            )}

            {expandedSection === 'status' && (
              <StatusInfoPanel
                website={website}
                isEditMode={isEditMode}
                editForm={editForm}
                setEditForm={setEditForm}
                getStatusBadge={getStatusBadge}
              />
            )}

            {expandedSection === 'network' && (
              <NetworkConnectionsPanel
                website={website}
                onAddLink={() => setIsAddLinkModalOpen(true)}
                onDeleteLink={handleDeleteLink}
                deletingLinkId={deletingLinkId}
              />
            )}

            {expandedSection === 'tasks' && (
              <TasksPanel
                website={website}
                expandedSection={expandedSection}
                toggleSection={toggleSection}
              />
            )}
          </div>
          <div className="lg:col-span-1" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <QuickActionsPanel
              website={website}
              onAddLink={() => setIsAddLinkModalOpen(true)}
              onDelete={() => setIsConfirmDeleteOpen(true)}
            />
          </div>
        </div>

        {/* Add Link Panel */}
        <SidePanel
          open={isAddLinkModalOpen}
          onClose={() => setIsAddLinkModalOpen(false)}
          width={420}
          eyebrow="Network Connections"
          title="Add Outgoing Link"
          footer={
            availableWebsites.length > 0 ? (
              <>
                <Button
                  icon="check"
                  onClick={handleAddLink}
                  disabled={isSubmitting || !selectedTargetId}
                >
                  {isSubmitting ? 'Adding...' : 'Add Link'}
                </Button>
                <Button variant="ghost" onClick={() => setIsAddLinkModalOpen(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setIsAddLinkModalOpen(false)}>
                Close
              </Button>
            )
          }
        >
          {availableWebsites.length === 0 ? (
            <div className="text-center" style={{ padding: 'var(--space-8) 0' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                No available websites to link to.
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                All websites are already linked or there are no other websites in the system.
              </p>
            </div>
          ) : (
            <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                  {website?.title || formatUrl(website?.url || "")}
                </span>
                <Icon name="arrow-right" size={13} style={{ color: 'var(--text-faint)' }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Target website</span>
              </div>

              <WebsiteDropdown
                websites={availableWebsites}
                selectedId={selectedTargetId}
                onChange={setSelectedTargetId}
                formatUrl={formatUrl}
              />
            </div>
          )}
        </SidePanel>

        {/* Delete Confirmation Modal */}
        {isConfirmDeleteOpen && (
          <div
            className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad"
            style={{ zIndex: 50, background: 'var(--surface-overlay)' }}
          >
            <div
              className="max-w-md w-full ck-menu-pop"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-pop)',
                padding: 'var(--space-6)',
              }}
            >
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Private Network</div>
              <h3
                style={{
                  margin: '0 0 var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  letterSpacing: 'var(--tracking-tight)',
                  color: 'var(--text-body)',
                }}
              >
                Delete Website
              </h3>

              <p style={{ margin: '0 0 var(--space-6)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)' }}>
                Are you sure you want to delete "{website?.title || formatUrl(website?.url || "")}"?
                This will also remove all its connections and cannot be undone.
              </p>

              <div className="flex justify-end" style={{ gap: '10px' }}>
                <Button variant="ghost" onClick={() => setIsConfirmDeleteOpen(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="danger" icon="trash-2" onClick={handleDeleteWebsite} disabled={isSubmitting}>
                  {isSubmitting ? 'Deleting...' : 'Delete Website'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
